import React from "react";
import { Camera, CameraOff, History, ArrowRight, Sparkles, Bell, X } from "lucide-react";
import { api } from "../../lib/api";
import StickyFooterCTA from "../ui/StickyFooterCTA";

const SESSION_TYPES = [
  { id: "initial_evaluation", label: "Initial Evaluation", desc: "First visit, full baseline" },
  { id: "follow_up", label: "Follow-Up Treatment", desc: "Routine treatment session" },
  { id: "progress_reassessment", label: "Progress Reassessment", desc: "Measure progress against baseline" },
  { id: "maintenance", label: "Maintenance Session", desc: "Ongoing wellness / prevention" },
  { id: "discharge", label: "Discharge Session", desc: "Final visit / treatment complete" },
];

export const PHOTO_MODES = {
  none: { id: "none", label: "No photos this session", icon: CameraOff, hint: "Quick visit — skip photo capture entirely" },
  new: { id: "new", label: "Take new photos", icon: Camera, hint: "Front, side, back posture photos" },
  previous: { id: "previous", label: "Use previous photos for comparison", icon: History, hint: "Re-attach prior photos + AI comparison narrative" },
};

const REMIND_KEY = (clientId) => `posturra.photoReminderSnoozedUntil.${clientId}`;
export const REMIND_EOS_KEY = (clientId) => `posturra.photoRemindEndOfSession.${clientId}`;
const SNOOZE_DAYS = 7;

export default function SessionSetupSection({
  clientId,
  sessionType,
  setSessionType,
  photoMode,
  setPhotoMode,
  comparedFromSessionId,
  setComparedFromSessionId,
  setPhase,
  onAutoDetected,
  hasPrevious = false,
  assessmentMode = "continue",
  setAssessmentMode = () => {},
}) {
  const [recommendation, setRecommendation] = React.useState(null);
  const [recDismissed, setRecDismissed] = React.useState(false);
  const [priorPhotoSessions, setPriorPhotoSessions] = React.useState([]);
  const continueMode = assessmentMode === "full" ? "full" : "continue";

  // Pull recommendation + timeline on mount
  React.useEffect(() => {
    let mounted = true;
    api.get(`/clients/${clientId}/photo-recommendation`).then((r) => {
      if (!mounted) return;
      setRecommendation(r.data);
      // Auto-detect session type unless the user has already set one explicitly via override
      if (r.data?.suggested_session_type) {
        onAutoDetected?.(r.data.suggested_session_type);
      }
    }).catch(() => {});
    api.get(`/clients/${clientId}/photo-timeline`).then((r) => {
      if (!mounted) return;
      setPriorPhotoSessions((r.data || []).filter((s) => s.has_photos));
    }).catch(() => {});
    // Snooze check
    try {
      const until = parseInt(localStorage.getItem(REMIND_KEY(clientId)) || "0", 10);
      if (until && Date.now() < until) setRecDismissed(true);
    } catch { /* ignore */ }
    return () => { mounted = false; };
  }, [clientId, onAutoDetected]);

  const snoozeReminder = () => {
    if (recommendation?.milestone) {
      // Milestone checkpoints resurface at the END of this session, not in 7 days.
      try { localStorage.setItem(REMIND_EOS_KEY(clientId), String(recommendation.milestone)); } catch { /* ignore */ }
      setRecDismissed(true);
      return;
    }
    try {
      const until = Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000;
      localStorage.setItem(REMIND_KEY(clientId), String(until));
    } catch { /* ignore */ }
    setRecDismissed(true);
  };

  const skipCheckpoint = async () => {
    if (recommendation?.milestone) {
      try { await api.post(`/clients/${clientId}/photo-checkpoint/skip`, { milestone: recommendation.milestone }); } catch { /* non-fatal */ }
    }
    setRecDismissed(true);
  };

  const dismissReminder = () => setRecDismissed(true);
  const isMilestone = !!recommendation?.milestone;

  const showRecBanner = !!recommendation?.recommended && !recDismissed && photoMode !== "new";

  return (
    <div data-testid="session-setup-phase">
      <div className="label-overline mb-2">Step 0 — Session setup</div>
      <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-ink-900 mb-2">
        What kind of session is this?
      </h1>
      <p className="text-ink-700 mb-8 max-w-2xl">
        We've auto-detected the most likely session type based on visit history. You can change either
        the session type or the photo workflow before continuing.
      </p>

      {/* Iter BE — returning clients default to continuing care, not a new intake */}
      {hasPrevious && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 mb-6" data-testid="continue-mode-chooser">
          <button
            type="button"
            data-testid="continue-from-last-btn"
            onClick={() => setAssessmentMode("continue")}
            className={`text-left p-5 rounded-3xl border-2 transition-all ${
              continueMode === "continue" ? "border-sage-500 bg-sage-500/10 ring-1 ring-sage-500" : "border-paper-200 bg-white hover:bg-paper-50"
            }`}
          >
            <div className={`font-medium ${continueMode === "continue" ? "text-sage-700" : "text-ink-900"}`}>
              Continue from last session <span className="text-[10px] uppercase tracking-wider font-semibold ml-1 px-2 py-0.5 rounded-full bg-sage-500/15 text-sage-700">Default</span>
            </div>
            <div className="text-xs text-ink-500 mt-1">Quick "what changed?" check-in — no repeated intake. Photos only if a checkpoint is due.</div>
          </button>
          <button
            type="button"
            data-testid="full-assessment-btn"
            onClick={() => {
              setAssessmentMode("full");
              setSessionType("progress_reassessment");
              setPhotoMode("new");
            }}
            className={`text-left p-5 rounded-3xl border-2 transition-all ${
              continueMode === "full" ? "border-terracotta-500 bg-terracotta-500/5 ring-1 ring-terracotta-500" : "border-paper-200 bg-white hover:bg-paper-50"
            }`}
          >
            <div className={`font-medium ${continueMode === "full" ? "text-terracotta-600" : "text-ink-900"}`}>Full assessment</div>
            <div className="text-xs text-ink-500 mt-1">New photos + full reassessment — when clinically appropriate (new injury, major change).</div>
          </button>
        </div>
      )}

      {/* Smart photo recommendation banner */}
      {showRecBanner && (
        <div data-testid="photo-rec-banner" className="rounded-3xl border border-terracotta-500/40 bg-terracotta-500/5 p-5 mb-6 flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-terracotta-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium text-ink-900 mb-1">{isMilestone ? "Progress photos due" : "Photos recommended for this visit"}</div>
            <div className="text-sm text-ink-700 mb-1" data-testid="photo-rec-reason">{recommendation.reason}</div>
            {isMilestone && recommendation.compare_with_label && (
              <div className="text-xs text-sage-700 font-medium mb-3" data-testid="photo-rec-compare-hint">{recommendation.compare_with_label}</div>
            )}
            <div className="flex flex-wrap gap-2 mt-2">
              <button
                onClick={() => setPhotoMode("new")}
                data-testid="photo-rec-take"
                className="min-h-[36px] px-4 rounded-full bg-ink-900 text-white text-xs font-medium hover:bg-ink-800 inline-flex items-center gap-1.5"
              >
                <Camera className="w-3.5 h-3.5" /> Take photos now
              </button>
              <button
                onClick={snoozeReminder}
                data-testid="photo-rec-remind"
                className="min-h-[36px] px-4 rounded-full bg-white border border-paper-200 text-ink-700 text-xs hover:bg-paper-100 inline-flex items-center gap-1.5"
              >
                <Bell className="w-3.5 h-3.5" /> Remind me later
              </button>
              <button
                onClick={isMilestone ? skipCheckpoint : dismissReminder}
                data-testid="photo-rec-skip"
                className="min-h-[36px] px-4 rounded-full bg-white border border-paper-200 text-ink-700 text-xs hover:bg-paper-100"
              >
                {isMilestone ? "Skip this checkpoint" : "Skip this time"}
              </button>
            </div>
          </div>
          <button onClick={dismissReminder} className="p-1 rounded-full hover:bg-paper-100" aria-label="Dismiss" data-testid="photo-rec-close">
            <X className="w-4 h-4 text-ink-500" />
          </button>
        </div>
      )}

      {/* Session type selector */}
      <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8 mb-6">
        <div className="label-overline mb-3">Session type</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="session-type-grid">
          {SESSION_TYPES.map((t) => {
            const active = sessionType === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setSessionType(t.id)}
                data-testid={`session-type-${t.id}`}
                className={`text-left p-4 rounded-2xl border transition-all min-h-[72px] ${
                  active
                    ? "border-sage-500 bg-sage-500/10 ring-1 ring-sage-500"
                    : "border-paper-200 bg-white hover:border-paper-300 hover:bg-paper-50"
                }`}
              >
                <div className={`font-medium ${active ? "text-sage-700" : "text-ink-900"}`}>{t.label}</div>
                <div className="text-xs text-ink-500 mt-0.5">{t.desc}</div>
              </button>
            );
          })}
        </div>
        {recommendation?.visit_count != null && (
          <div className="text-xs text-ink-500 mt-3" data-testid="visit-count">
            Visit #{recommendation.visit_count + 1}
            {recommendation.last_photo_date && (
              <> · last photos {recommendation.visits_since_last_photo} visit{recommendation.visits_since_last_photo === 1 ? "" : "s"} ago</>
            )}
          </div>
        )}
      </div>

      {/* Photo workflow chooser */}
      <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8 mb-6">
        <div className="label-overline mb-3">Photo workflow</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5" data-testid="photo-mode-grid">
          {Object.values(PHOTO_MODES).map((m) => {
            const Icon = m.icon;
            const active = photoMode === m.id;
            const disabled = m.id === "previous" && priorPhotoSessions.length === 0;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => !disabled && setPhotoMode(m.id)}
                data-testid={`photo-mode-${m.id}`}
                disabled={disabled}
                className={`text-left p-5 rounded-2xl border transition-all min-h-[120px] ${
                  active
                    ? "border-terracotta-500 bg-terracotta-500/5 ring-1 ring-terracotta-500"
                    : disabled
                    ? "border-paper-200 bg-paper-100/40 opacity-50 cursor-not-allowed"
                    : "border-paper-200 bg-white hover:border-paper-300 hover:bg-paper-50"
                }`}
              >
                <Icon className={`w-5 h-5 mb-2 ${active ? "text-terracotta-600" : "text-ink-700"}`} />
                <div className={`font-medium ${active ? "text-terracotta-600" : "text-ink-900"}`}>{m.label}</div>
                <div className="text-xs text-ink-500 mt-1">{m.hint}</div>
                {disabled && <div className="text-[11px] text-ink-500 mt-2 italic">No previous photos available</div>}
              </button>
            );
          })}
        </div>

        {/* Previous photos picker */}
        {photoMode === "previous" && priorPhotoSessions.length > 0 && (
          <div className="mt-5 pt-5 border-t border-paper-200" data-testid="previous-photo-picker">
            <div className="label-overline mb-2">Choose which previous photo session to reference</div>
            <div className="space-y-1.5">
              {priorPhotoSessions.slice().reverse().slice(0, 6).map((s) => {
                const active = comparedFromSessionId === s.session_id;
                return (
                  <button
                    key={s.session_id}
                    type="button"
                    onClick={() => setComparedFromSessionId(s.session_id)}
                    data-testid={`previous-photo-option-${s.session_id}`}
                    className={`block w-full text-left px-4 py-3 rounded-2xl border transition-all ${
                      active ? "border-sage-500 bg-sage-500/10" : "border-paper-200 bg-white hover:bg-paper-50"
                    }`}
                  >
                    <div className="text-sm text-ink-900 font-medium">
                      Visit #{s.visit_number} · {new Date(s.date).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-ink-500 mt-0.5">
                      {(s.session_type || "follow_up").replace(/_/g, " ")}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Continue */}
      <StickyFooterCTA testid="setup-footer">
        <button
          onClick={() => setPhase(photoMode === "new" ? "capture" : "intake")}
          disabled={photoMode === "previous" && !comparedFromSessionId}
          data-testid="setup-continue-btn"
          className="min-h-[48px] px-6 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continue <ArrowRight className="w-4 h-4" />
        </button>
      </StickyFooterCTA>
    </div>
  );
}
